import React, { useEffect, useState, useCallback } from 'react';
import { Timeline, Tag, Button, Space, Input, Spin, Typography, message } from 'antd';
import { CheckOutlined, CloseOutlined, RightOutlined } from '@ant-design/icons';
import { workflowApi } from '../services/api';
import useAuthStore from '../store/authStore';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { TextArea } = Input;

export default function WorkflowTimeline({ instanceId }) {
  const user = useAuthStore((s) => s.user);
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  const fetchInstance = useCallback(async () => {
    if (!instanceId) return;
    setLoading(true);
    try {
      const res = await workflowApi.getInstance(instanceId);
      setInstance(res);
    } catch (e) {
      message.error(e?.error || '加载工作流失败');
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    fetchInstance();
  }, [fetchInstance]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await workflowApi.approve(instanceId, { comment: approveComment || undefined });
      message.success('审批通过');
      setApproveComment('');
      fetchInstance();
    } catch (e) {
      message.error(e?.error || '操作失败');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      message.warning('驳回必须填写意见');
      return;
    }
    setRejecting(true);
    try {
      await workflowApi.reject(instanceId, { comment: rejectComment });
      message.success('已驳回');
      setRejectComment('');
      setShowRejectInput(false);
      fetchInstance();
    } catch (e) {
      message.error(e?.error || '操作失败');
    } finally {
      setRejecting(false);
    }
  };

  if (loading) return <Spin tip="加载中..." />;
  if (!instance) return <Text type="secondary">未找到工作流实例</Text>;

  const stages = JSON.parse(instance.template.stages);
  const actions = instance.actions || [];

  // 获取每个阶段的操作记录
  const getStageAction = (stageOrder) => {
    return actions.find((a) => a.stage === stageOrder);
  };

  // 判断当前用户是否可以操作当前阶段
  const canOperate = () => {
    if (instance.status !== 'active') return false;
    const currentStage = stages.find((s) => s.order === instance.currentStage);
    if (!currentStage) return false;
    if (currentStage.type === 'auto') return false;
    if (currentStage.role && user?.role !== currentStage.role && user?.role !== 'admin') return false;
    return true;
  };

  const timelineItems = stages.map((stage) => {
    const stageAction = getStageAction(stage.order);
    let color = 'gray';
    let label = '';

    if (stage.order < instance.currentStage) {
      color = 'green';
      label = stage.type === 'auto' ? '已完成' : '已通过';
    } else if (stage.order === instance.currentStage) {
      if (instance.status === 'completed') {
        color = 'green';
        label = '已完成';
      } else if (instance.status === 'rejected') {
        color = 'red';
        label = '已驳回';
      } else {
        color = 'blue';
        label = stage.type === 'auto' ? '自动处理中' : '当前阶段';
      }
    }

    const stageLabel = (
      <Tag color={
        color === 'green' ? 'success' :
        color === 'blue' ? 'processing' :
        color === 'red' ? 'error' : 'default'
      }>
        {stage.type === 'approval' ? '审批' :
         stage.type === 'manual' ? '手动' :
         stage.type === 'auto' ? '自动' : stage.type}
      </Tag>
    );

    return {
      color,
      dot: color === 'blue' ? <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#1890ff', border: '2px solid #91caff' }} /> : undefined,
      children: (
        <div style={{ paddingBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Text strong>{stage.name}</Text>
            {stageLabel}
            {label && <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>}
          </div>
          {stage.role && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              负责人角色：{stage.role === 'admin' ? '管理员' : stage.role === 'manager' ? '主管' : '销售'}
            </div>
          )}
          {stageAction && (
            <div style={{ marginTop: 4, background: '#fafafa', padding: '8px 12px', borderRadius: 6 }}>
              <Space>
                <Tag color={
                  stageAction.action === 'approve' ? 'success' :
                  stageAction.action === 'reject' ? 'error' : 'blue'
                }>
                  {stageAction.action === 'approve' ? '通过' :
                   stageAction.action === 'reject' ? '驳回' : '完成'}
                </Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {stageAction.user?.name} · {dayjs(stageAction.createdAt).format('MM-DD HH:mm')}
                </Text>
              </Space>
              {stageAction.comment && (
                <div style={{ marginTop: 4, fontSize: 13, color: '#555' }}>
                  "{stageAction.comment}"
                </div>
              )}
            </div>
          )}
        </div>
      ),
    };
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {instance.template.name}
            {instance.status === 'completed' && <Tag color="success" style={{ marginLeft: 8 }}>已完成</Tag>}
            {instance.status === 'rejected' && <Tag color="error" style={{ marginLeft: 8 }}>已驳回</Tag>}
            {instance.status === 'active' && <Tag color="processing" style={{ marginLeft: 8 }}>进行中</Tag>}
          </Title>
        </div>
      </div>

      <Timeline items={timelineItems} />

      {canOperate() && (
        <div style={{ marginTop: 16, padding: '16px', background: '#e6f7ff', borderRadius: 8, border: '1px solid #91d5ff' }}>
          <div style={{ marginBottom: 8 }}>
            <TextArea
              placeholder="审批意见（选填）"
              value={approveComment}
              onChange={(e) => setApproveComment(e.target.value)}
              rows={2}
              style={{ marginBottom: 8 }}
            />
          </div>
          <Space>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={approving}
              onClick={handleApprove}
            >
              通过
            </Button>
            {!showRejectInput ? (
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => setShowRejectInput(true)}
              >
                驳回
              </Button>
            ) : (
              <Space>
                <Input
                  placeholder="驳回意见（必填）"
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  style={{ width: 200 }}
                />
                <Button
                  danger
                  loading={rejecting}
                  onClick={handleReject}
                >
                  确认驳回
                </Button>
                <Button onClick={() => { setShowRejectInput(false); setRejectComment(''); }}>
                  取消
                </Button>
              </Space>
            )}
          </Space>
        </div>
      )}
    </div>
  );
}
